import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { PermissionType, TrialImplementationStatus } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { InvoicesGridFormatterService } from '../invoices-grid-formatter.service';

export interface BulkEditInvoicesResponse {
  data: {
    accrualPeriod: null | string;
    paymentStatus: null | string;
    purchaseOrder: null | string;
    servicesPeriod: null | string;
    status: null | string;
    vendor: null | string;
  };
  success: boolean;
}

@Component({
  imports: [NgClass, NgSelectModule, FormsModule],
  templateUrl: 'bulk-edit-invoices.component.html',
})
export class BulkEditInvoicesComponent {
  authService = inject(AuthService);
  invoiceService = inject(InvoiceService);
  invoicesGridFormatterService = inject(InvoicesGridFormatterService);
  launchDarklyService = inject(LaunchDarklyService);
  mainQuery = inject(MainQuery);
  ref = inject(CustomOverlayRef<BulkEditInvoicesResponse>);

  purchaseOrderOptions = signal<Option<null | string>[]>([{ label: Utils.zeroHyphen, value: '' }]);

  accrualPeriodOptions = computed(() => {
    const { currentOpenMonth } = this.mainQuery.mainState();
    const trial = this.mainQuery.selectedTrial();
    const isTrialArchivedOrLive =
      trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
      trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE;
    const options = isTrialArchivedOrLive
      ? this.invoiceService.accrualPeriodOptions.filter((option) => {
          return `${option.value}-01` >= currentOpenMonth;
        })
      : this.invoiceService.accrualPeriodOptions;

    return [{ label: Utils.zeroHyphen, value: '' }, ...options];
  });

  userHasApproveInvoicePermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_APPROVE_INVOICE],
  });

  invoiceStatusOptions = computed(() => {
    const hasPermission = this.userHasApproveInvoicePermission();

    return hasPermission
      ? this.invoiceService.invoiceStatusOptions.map((status) => ({
          value: status,
          ...InvoicesGridFormatterService.getInvoiceStatusStyles(status),
        }))
      : [];
  });

  accrualPeriod = model<null | string>(null);
  paymentStatus = model<null | string>(null);
  purchaseOrder = model<null | string>(null);
  servicesPeriod = model<null | string>(null);
  status = model<null | string>(null);
  vendor = model<null | string>(null);

  isApplyButtonDisabled = computed(() => {
    return (
      this.vendor() == null &&
      this.status() == null &&
      this.paymentStatus() == null &&
      this.accrualPeriod() == null &&
      this.servicesPeriod() == null &&
      this.purchaseOrder() == null
    );
  });
  paymentStatusOptions = computed(() => {
    return this.invoiceService.paymentStatusOptions.map((status) => ({
      value: status || '',
      ...InvoicesGridFormatterService.getInvoicePaymentStatusStyles(status),
    }));
  });
  servicesPeriodOptions = computed(() => {
    return [{ label: Utils.zeroHyphen, value: '' }, ...this.invoiceService.accrualPeriodOptions];
  });
  vendorOptions = computed(() => {
    return this.invoiceService.vendorOptions;
  });

  showInvoicesPaymentStatus = this.launchDarklyService.$select(
    (flags) => flags.invoices_payment_status
  );
  updatePOWhenVendorChanges = effect(() => {
    this.purchaseOrder.set(null);

    if (this.vendor()) {
      this.purchaseOrderOptions.set([
        { label: Utils.zeroHyphen, value: '' },
        ...this.invoiceService.purchaseOrdersQuery
          .getAll()
          .filter((order) => order.organization?.id === this.vendor())
          .map(({ id }) => ({
            label: this.invoicesGridFormatterService.getFormattedPurchaseOrder(id),
            value: id,
          })),
      ]);
    } else {
      this.purchaseOrderOptions.set([{ label: Utils.zeroHyphen, value: '' }]);
    }
  });

  onSubmit() {
    this.ref.close({
      data: {
        accrualPeriod: this.accrualPeriod(),
        paymentStatus: this.paymentStatus(),
        purchaseOrder: this.purchaseOrder(),
        servicesPeriod: this.servicesPeriod(),
        status: this.status(),
        vendor: this.vendor(),
      },
      success: true,
    });
  }
}
